/**
 * Nova Derma — numeric display primitives (DS-11, DS-24, DS-51).
 *
 * Every amount, quantity and percentage the user reads goes through one of
 * these. They exist so that a column of numbers stays monospaced, tabular and
 * left-to-right even though the sentence around it runs right-to-left, and so
 * that "three decimals" for the Jordanian Dinar is decided in exactly one place.
 */

import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { formatMoney, formatPercent, formatQuantity, roundTo } from '@nova/shared';
import { cn } from '../../lib/utils';

/** DS-10/DS-13 — the only sizes a number is allowed to take in this system. */
export type NumericSize = 'micro' | 'tiny' | 'base' | 'lg' | 'kpi';

const SIZE_CLASS: Record<NumericSize, string> = {
  micro: 'text-[10px]',
  tiny: 'text-[11px]',
  base: 'text-xs',
  lg: 'text-sm',
  kpi: 'text-2xl',
};

/** DS-11 + DS-51 — monospace, tabular figures, isolated LTR run. */
const NUM_BASE = 'font-mono tabular-nums ltr-num';

const EM_DASH = '—';

/**
 * Decimals arrive from the API as strings (Prisma `Decimal` is serialised that
 * way whenever precision matters), so every component accepts both.
 */
function toNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? n : null;
}

interface NumericBaseProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  bold?: boolean;
  size?: NumericSize;
  /** Rendered when the value is null/undefined/unparsable. */
  placeholder?: string;
}

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

export interface MoneyProps extends NumericBaseProps {
  value: number | string | null | undefined;
  /** Append the currency symbol (`د.أ` / `JD`). Off inside table columns. */
  showSymbol?: boolean;
  lang?: 'AR' | 'EN';
  /** DS-24 — a negative amount is red-500. */
  colorNegative?: boolean;
}

/**
 * DS-11 — the Jordanian Dinar carries **three** decimals (fils). `formatMoney`
 * owns that constant; never format an amount by hand.
 */
export const Money = forwardRef<HTMLSpanElement, MoneyProps>(function Money(
  {
    value,
    showSymbol = false,
    lang = 'AR',
    colorNegative = true,
    bold = false,
    size = 'base',
    placeholder = EM_DASH,
    className,
    ...rest
  },
  ref,
) {
  const n = toNumber(value);

  if (n === null) {
    return (
      <span ref={ref} className={cn(NUM_BASE, SIZE_CLASS[size], 'text-slate-400', className)} {...rest}>
        {placeholder}
      </span>
    );
  }

  return (
    <span
      ref={ref}
      className={cn(
        NUM_BASE,
        SIZE_CLASS[size],
        bold && 'font-bold',
        colorNegative && n < 0 && 'text-red-500',
        className,
      )}
      {...rest}
    >
      {formatMoney(n, showSymbol, lang)}
    </span>
  );
});

// ---------------------------------------------------------------------------
// Num
// ---------------------------------------------------------------------------

export interface NumProps extends NumericBaseProps {
  value: number | string | null | undefined;
  /** Fixed fraction digits; 0 renders whole numbers. */
  decimals?: number;
  /** Thousands separators. Turn off for ids, years and codes. */
  grouping?: boolean;
  colorNegative?: boolean;
}

/** A generic counted number — visit counts, session numbers, stock days. */
export const Num = forwardRef<HTMLSpanElement, NumProps>(function Num(
  {
    value,
    decimals = 0,
    grouping = true,
    colorNegative = true,
    bold = false,
    size = 'base',
    placeholder = EM_DASH,
    className,
    ...rest
  },
  ref,
) {
  const n = toNumber(value);

  if (n === null) {
    return (
      <span ref={ref} className={cn(NUM_BASE, SIZE_CLASS[size], 'text-slate-400', className)} {...rest}>
        {placeholder}
      </span>
    );
  }

  const text = roundTo(n, decimals).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: grouping,
  });

  return (
    <span
      ref={ref}
      className={cn(
        NUM_BASE,
        SIZE_CLASS[size],
        bold && 'font-bold',
        colorNegative && n < 0 && 'text-red-500',
        className,
      )}
      {...rest}
    >
      {text}
    </span>
  );
});

// ---------------------------------------------------------------------------
// Percent
// ---------------------------------------------------------------------------

export interface PercentProps extends NumericBaseProps {
  /** On a 0–100 scale: `82.5` renders `82.5%`. */
  value: number | string | null | undefined;
  decimals?: number;
  colorNegative?: boolean;
}

/** Collection rate, occupancy, margin — anything already scaled to 0–100. */
export const Percent = forwardRef<HTMLSpanElement, PercentProps>(function Percent(
  {
    value,
    decimals = 1,
    colorNegative = true,
    bold = false,
    size = 'base',
    placeholder = EM_DASH,
    className,
    ...rest
  },
  ref,
) {
  const n = toNumber(value);

  if (n === null) {
    return (
      <span ref={ref} className={cn(NUM_BASE, SIZE_CLASS[size], 'text-slate-400', className)} {...rest}>
        {placeholder}
      </span>
    );
  }

  return (
    <span
      ref={ref}
      className={cn(
        NUM_BASE,
        SIZE_CLASS[size],
        bold && 'font-bold',
        colorNegative && n < 0 && 'text-red-500',
        className,
      )}
      {...rest}
    >
      {formatPercent(n, decimals)}
    </span>
  );
});

// ---------------------------------------------------------------------------
// QuantityText
// ---------------------------------------------------------------------------

export interface QuantityTextProps extends NumericBaseProps {
  value: number | string | null | undefined;
  /** Arabic unit label — "فيال", "وحدة", "مل". Rendered muted beside the number. */
  unit?: string | null;
  /** Negative stock is a real condition worth flagging (INV-83). */
  colorNegative?: boolean;
}

/**
 * Stock screens read "12 فيال" and "1,200 وحدة" all day, so the number keeps its
 * LTR monospaced run and the unit stays plain Arabic next to it. Trailing zeros
 * are dropped by `formatQuantity` — "2" beats "2.0000" in a grid.
 */
export const QuantityText = forwardRef<HTMLSpanElement, QuantityTextProps>(function QuantityText(
  {
    value,
    unit,
    colorNegative = true,
    bold = false,
    size = 'base',
    placeholder = EM_DASH,
    className,
    ...rest
  },
  ref,
) {
  const n = toNumber(value);

  if (n === null) {
    return (
      <span ref={ref} className={cn(NUM_BASE, SIZE_CLASS[size], 'text-slate-400', className)} {...rest}>
        {placeholder}
      </span>
    );
  }

  return (
    <span
      ref={ref}
      className={cn('inline-flex items-baseline gap-1', SIZE_CLASS[size], className)}
      {...rest}
    >
      <span className={cn(NUM_BASE, bold && 'font-bold', colorNegative && n < 0 && 'text-red-500')}>
        {formatQuantity(n)}
      </span>
      {unit ? <span className="text-slate-500">{unit}</span> : null}
    </span>
  );
});

// ---------------------------------------------------------------------------
// DeltaValue
// ---------------------------------------------------------------------------

export type DeltaFormat = 'percent' | 'number' | 'money';

export interface DeltaValueProps extends NumericBaseProps {
  /** The change itself, signed. `KpiValue.deltaPercent` drops straight in here. */
  value: number | string | null | undefined;
  format?: DeltaFormat;
  decimals?: number;
  /**
   * For costs, no-shows and cancellation rates a rise is bad — `invert` swaps the
   * colours while the arrow keeps following the real sign (COST-14, APT-14).
   */
  invert?: boolean;
  /** e.g. "مقارنة بالشهر الماضي". Rendered under the value when provided. */
  caption?: string;
  showArrow?: boolean;
  lang?: 'AR' | 'EN';
}

export const DeltaValue = forwardRef<HTMLSpanElement, DeltaValueProps>(function DeltaValue(
  {
    value,
    format = 'percent',
    decimals = 1,
    invert = false,
    caption,
    showArrow = true,
    lang = 'AR',
    bold = true,
    size = 'base',
    placeholder = EM_DASH,
    className,
    ...rest
  },
  ref,
) {
  const n = toNumber(value);

  if (n === null) {
    return (
      <span ref={ref} className={cn(NUM_BASE, SIZE_CLASS[size], 'text-slate-400', className)} {...rest}>
        {placeholder}
      </span>
    );
  }

  const magnitude = Math.abs(n);
  const flat = roundTo(n, decimals) === 0;
  const rising = n > 0;
  // `invert` only re-reads the sign; it never lies about the direction.
  const favourable = flat ? null : invert ? !rising : rising;

  const tone = favourable === null ? 'text-slate-500' : favourable ? 'text-emerald-600' : 'text-red-600';
  const Arrow = flat ? Minus : rising ? ArrowUp : ArrowDown;

  let text: string;
  if (format === 'money') text = formatMoney(magnitude, false, lang);
  else if (format === 'percent') text = formatPercent(magnitude, decimals);
  else {
    text = roundTo(magnitude, decimals).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  const sign = flat ? '' : rising ? '+' : '-';

  return (
    <span ref={ref} className={cn('inline-flex flex-col items-start gap-0.5', className)} {...rest}>
      <span className={cn('inline-flex items-center gap-1', SIZE_CLASS[size], tone)}>
        {showArrow ? <Arrow className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : null}
        <span className={cn(NUM_BASE, bold && 'font-bold')}>
          {sign}
          {text}
        </span>
      </span>
      {caption ? <span className="text-[10px] text-slate-500">{caption}</span> : null}
    </span>
  );
});
